import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2 } from "lucide-react";
import { CONTACT } from "../data/mock";

const API = process.env.REACT_APP_BACKEND_URL;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const initialForm = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/contact`, form);
      toast.success("Message sent! We'll get back to you within 24 hours.");
      setForm(initialForm);
    } catch (err) {
      console.error("Contact submission failed", err);
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-[#6C0B1B]/15 bg-white px-4 py-3 text-[15px] text-[#1A1014] placeholder:text-[#5C4B51]/50 focus:outline-none focus:border-[#6C0B1B] focus:ring-2 focus:ring-[#6C0B1B]/10 transition-colors duration-200";

  return (
    <div data-testid="contact-page" className="pt-16">
      <section className="bg-[#FDFBF7] py-24 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-3xl mb-16">
            <p className="uppercase text-xs tracking-[0.2em] text-[#A31621] font-semibold mb-4">Stay Connected</p>
            <h1 className="font-heading text-5xl sm:text-6xl font-light tracking-tight leading-[1.05] text-[#1A1014]">
              Let's talk about <span className="italic text-[#6C0B1B]">your growth.</span>
            </h1>
            <p className="mt-6 text-lg text-[#5C4B51] leading-relaxed">
              Reach out for tailored digital solutions — or just to say hello. We reply within one business day.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {/* Form */}
            <motion.form {...fadeUp} onSubmit={submit} data-testid="contact-form"
              className="lg:col-span-7 bg-white border border-[#6C0B1B]/10 rounded-2xl p-8 sm:p-10 shadow-[0_20px_40px_-15px_rgba(108,11,27,0.08)]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm font-medium text-[#1A1014] mb-1.5 block">Full Name *</label>
                  <input data-testid="contact-input-name" required value={form.name} onChange={set("name")} placeholder="Your full name" className={inputCls} />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1014] mb-1.5 block">Email *</label>
                  <input data-testid="contact-input-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1014] mb-1.5 block">Phone</label>
                  <input data-testid="contact-input-phone" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className={inputCls} />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#1A1014] mb-1.5 block">Subject</label>
                  <input data-testid="contact-input-subject" value={form.subject} onChange={set("subject")} placeholder="Website, SEO, Marketing..." className={inputCls} />
                </div>
              </div>
              <div className="mt-5">
                <label className="text-sm font-medium text-[#1A1014] mb-1.5 block">Message *</label>
                <textarea data-testid="contact-input-message" required rows={5} value={form.message} onChange={set("message")}
                  placeholder="Tell us about your business and goals..." className={inputCls} />
              </div>
              <button type="submit" disabled={loading} data-testid="contact-submit-button"
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#6C0B1B] text-white px-8 py-3.5 font-medium hover:bg-[#8C1126] disabled:opacity-60 transition-colors duration-200">
                {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                {loading ? "Sending..." : "Send Message"}
              </button>
            </motion.form>

            {/* Info */}
            <motion.div {...fadeUp} className="lg:col-span-5 flex flex-col gap-5">
              <div className="bg-[#6C0B1B] text-white rounded-2xl p-8">
                <h3 className="font-heading text-2xl font-semibold">Direct contact</h3>
                <div className="mt-5 flex flex-col gap-4 text-[15px]">
                  <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-3 hover:text-[#D4A24C] transition-colors duration-200">
                    <Mail size={17} className="text-[#D4A24C]" /> {CONTACT.email}
                  </a>
                  <a href={CONTACT.phoneLink} data-testid="contact-phone-link" className="flex items-center gap-3 hover:text-[#D4A24C] transition-colors duration-200">
                    <Phone size={17} className="text-[#D4A24C]" /> {CONTACT.phone}
                  </a>
                  <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link" className="flex items-center gap-3 hover:text-[#D4A24C] transition-colors duration-200">
                    <MessageCircle size={17} className="text-[#D4A24C]" /> Chat on WhatsApp
                  </a>
                </div>
              </div>
              <div className="bg-white border border-[#6C0B1B]/10 rounded-2xl p-8 shadow-[0_20px_40px_-15px_rgba(108,11,27,0.08)]">
                <h3 className="font-heading text-2xl font-semibold text-[#1A1014]">Our offices</h3>
                <div className="mt-5 flex flex-col gap-5">
                  {CONTACT.addresses.map((a) => (
                    <div key={a.label} data-testid={`contact-address-${a.label.split(" ")[0].toLowerCase()}`} className="flex gap-3">
                      <MapPin size={17} className="text-[#A31621] shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-[#1A1014]">{a.label}</p>
                        <p className="text-sm text-[#5C4B51] leading-relaxed mt-0.5">{a.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
